/**
 * General Equipment Guidance - EQUIPMENT REGISTER MAPPINGS
 * Maps the exact equipment names that appear in Equipment Register to WBC3 guidance
 * General equipment categories and miscellaneous items
 */

export const GENERAL_GUIDANCE = {
  // Medical Stores Category C
  'Medical Stores Category C': {
    id: 'Medical Stores Category C',
    name: 'Medical Stores Category C',
    category: 'Medical',
    regulation: 'WBC3 Section 22',
    description: 'Category C medical stores for workboat operations.',
    authority: 'WBC3 Section 22',
    placement: {
      requirements: [
        'Protected storage in accessible location',
        'List of contents displayed nearby',
        'Regular stock level monitoring',
        'Protection from moisture and contamination'
      ]
    },
    specifications: {
      type: 'Category C medical stores kit',
      standards: 'WBC3 Section 22 medical equipment requirements',
      contents: 'Prescribed medical supplies for Category C operations',
      accessibility: 'Readily accessible for emergency use'
    },
    maintenance: {
      mandatory: [
        'Monthly inventory and expiry date check',
        'Annual replacement of expired items',
        'Maintain complete inventory list',
        'Replenish used items immediately'
      ]
    }
  },

  // Garbage Receptacles
  'Garbage Receptacles': {
    id: 'Garbage Receptacles',
    name: 'Garbage Receptacles',
    category: 'Environmental',
    regulation: 'MARPOL Annex V',
    description: 'Garbage storage receptacles for waste management.',
    authority: 'MARPOL Annex V',
    placement: {
      requirements: [
        'Adequate capacity for voyage duration',
        'Separate receptacles for different waste types',
        'Secure storage to prevent overboard loss',
        'Accessible for crew use and disposal'
      ]
    },
    specifications: {
      type: 'Marine waste storage receptacles',
      standards: 'MARPOL Annex V waste management requirements',
      separation: 'Separate storage for plastics, organic waste, and general refuse',
      security: 'Secured against accidental overboard discharge'
    },
    maintenance: {
      mandatory: [
        'Regular emptying and cleaning',
        'Monthly inspection for damage or wear',
        'Ensure secure mounting and covers',
        'Replace damaged receptacles immediately'
      ]
    }
  },

  // Garbage Disposal Placards
  'Garbage Disposal Placards': {
    id: 'Garbage Disposal Placards',
    name: 'Garbage Disposal Placards',
    category: 'Environmental',
    regulation: 'MARPOL Annex V',
    description: 'Informational placards for garbage disposal requirements.',
    authority: 'MARPOL Annex V',
    placement: {
      requirements: [
        'Posted in conspicuous locations',
        'Clear and readable information',
        'Weather-protected mounting',
        'Multiple languages if required'
      ]
    },
    specifications: {
      type: 'MARPOL Annex V garbage disposal placards',
      standards: 'MARPOL Annex V information requirements',
      content: 'Waste disposal restrictions and requirements',
      visibility: 'Posted where crew can easily read and reference'
    },
    maintenance: {
      mandatory: [
        'Monthly inspection for readability and damage',
        'Replace faded or damaged placards',
        'Ensure secure mounting',
        'Update placards when regulations change'
      ]
    }
  },

  // Oil Pollution Prevention Equipment
  'Oil Pollution Prevention Equipment': {
    id: 'Oil Pollution Prevention Equipment',
    name: 'Oil Pollution Prevention Equipment',
    category: 'Environmental',
    regulation: 'MARPOL Annex I',
    description: 'Equipment for preventing and managing oil pollution.',
    authority: 'MARPOL Annex I',
    placement: {
      requirements: [
        'Oil spill prevention and containment equipment',
        'Accessible for emergency deployment',
        'Protected storage but readily available',
        'Appropriate for vessel size and operations'
      ]
    },
    specifications: {
      type: 'Oil pollution prevention and response equipment',
      standards: 'MARPOL Annex I pollution prevention requirements',
      capacity: 'Adequate for potential oil spill scenarios',
      deployment: 'Quick deployment capability for emergency response'
    },
    maintenance: {
      mandatory: [
        'Monthly equipment inspection and inventory',
        'Annual functionality test',
        'Replace expired or damaged items',
        'Service per manufacturer recommendations'
      ]
    }
  },

  // Fuel Spill Cleanup Equipment
  'Fuel Spill Cleanup Equipment': {
    id: 'Fuel Spill Cleanup Equipment',
    name: 'Fuel Spill Cleanup Equipment',
    category: 'General',
    regulation: 'WBC3 Environmental Requirements',
    description: 'Equipment for fuel spill cleanup and containment.',
    authority: 'WBC3 Environmental Requirements',
    placement: {
      requirements: [
        'Readily accessible near fuel handling areas',
        'Absorbent materials and containment equipment',
        'Protected storage but immediate availability',
        'Disposal bags and cleanup tools'
      ]
    },
    specifications: {
      type: 'Fuel spill cleanup and absorbent materials',
      standards: 'Environmental protection and cleanup requirements',
      materials: 'Oil absorbent pads, booms, and disposal materials',
      tools: 'Shovels, brushes, and containment equipment'
    },
    maintenance: {
      mandatory: [
        'Monthly inspection of cleanup materials',
        'Replenish used materials immediately',
        'Annual replacement of degraded absorbents',
        'Maintain complete cleanup kit inventory'
      ]
    }
  },

  // Fixed Fire Extinguishing System
  'Fixed Fire Extinguishing System': {
    id: 'Fixed Fire Extinguishing System',
    name: 'Fixed Fire Extinguishing System',
    category: 'General',
    regulation: 'WBC3 Section 16.4',
    description: 'Fixed fire extinguishing system for machinery or accommodation spaces.',
    authority: 'WBC3 Section 16.4',
    placement: {
      requirements: [
        'Fixed installation in protected space',
        'Remote activation from safe location',
        'Complete coverage of protected area',
        'Integration with ventilation and fuel systems'
      ]
    },
    specifications: {
      type: 'Fixed fire suppression system (CO2, foam, or water mist)',
      standards: 'WBC3 Section 16.4 fixed system requirements',
      activation: 'Manual activation from outside protected space',
      coverage: 'Complete protection of designated space'
    },
    maintenance: {
      mandatory: [
        'Annual system inspection and testing',
        'Monthly visual inspection of components',
        'Test activation controls and alarms',
        'Service per manufacturer recommendations'
      ]
    }
  },

  // Radar System
  'Radar System': {
    id: 'Radar System',
    name: 'Radar System',
    category: 'General',
    regulation: 'WBC3 Section 19.4',
    description: 'Radar system for navigation and collision avoidance.',
    authority: 'WBC3 Section 19.4',
    placement: {
      requirements: [
        'Clear 360-degree coverage capability',
        'Protected antenna installation',
        'Display unit at navigation position',
        'Performance monitoring capability'
      ]
    },
    specifications: {
      type: 'Marine radar system with display unit',
      standards: 'WBC3 Section 19.4 radar requirements',
      range: 'Appropriate range for vessel category and operations',
      performance: 'IMO performance standards for marine radar'
    },
    maintenance: {
      mandatory: [
        'Annual performance test and calibration',
        'Monthly visual inspection of antenna and cables',
        'Test all radar functions and alarms',
        'Service per manufacturer recommendations'
      ]
    }
  },

  // General Alarm System
  'General Alarm System': {
    id: 'General Alarm System',
    name: 'General Alarm System',
    category: 'General',
    regulation: 'WBC3 Section 20.1',
    description: 'General alarm system for emergency alerts.',
    authority: 'WBC3 Section 20.1',
    placement: {
      requirements: [
        'Audible throughout vessel',
        'Distinct emergency signal',
        'Multiple activation points',
        'Independent power supply or backup'
      ]
    },
    specifications: {
      type: 'General alarm system with emergency signals',
      standards: 'WBC3 Section 20.1 alarm requirements',
      coverage: 'Audible in all areas of vessel',
      signals: 'Distinct signals for different emergency types'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test of all signals',
        'Annual coverage area verification',
        'Test backup power supply',
        'Service per manufacturer recommendations'
      ]
    }
  },

  // Winch/Lifting Gear
  'Winch/Lifting Gear': {
    id: 'Winch/Lifting Gear',
    name: 'Winch/Lifting Gear',
    category: 'Operations',
    regulation: 'LOLER 1998',
    description: 'Winch and lifting gear for cargo and operational lifting.',
    authority: 'LOLER 1998',
    placement: {
      requirements: [
        'Secure mounting appropriate for loads',
        'Clear operating area and escape routes',
        'Load capacity marking clearly visible',
        'Safety devices and load limiting equipment'
      ]
    },
    specifications: {
      type: 'Marine winch or lifting gear with safety devices',
      standards: 'LOLER 1998 lifting operations requirements',
      capacity: 'Safe working load clearly marked',
      certification: 'Annual thorough examination certificate required'
    },
    maintenance: {
      mandatory: [
        'Daily pre-use visual inspection',
        'Weekly detailed inspection of wire ropes and gear',
        'Annual thorough examination by competent person',
        'Six-monthly intermediate inspection'
      ]
    }
  }
};

/**
 * Get General Equipment Guidance
 * @param {string} itemId - Equipment name/ID
 * @returns {Object|null} Guidance object or null
 */
export function getGeneralGuidance(itemId) {
  return GENERAL_GUIDANCE[itemId] || null;
}